/**
 * Admin Functions Service
 * Wrappers for Firebase Cloud Functions (callable functions)
 * These handle operations requiring Firebase Admin SDK
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../config/firebase';

const functions = getFunctions(app);

/**
 * Get all users with Firebase Auth metadata
 * Requires admin role
 */
export const getAllUsers = async () => {
  try {
    const adminGetAllUsers = httpsCallable(functions, 'adminGetAllUsers');
    const result = await adminGetAllUsers();
    return result.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    // Map Firebase function errors to user-friendly messages
    if (error.code === 'functions/unauthenticated') {
      throw new Error('You must be logged in to view users');
    }
    if (error.code === 'functions/permission-denied') {
      throw new Error('Admin privileges required');
    }
    throw error;
  }
};

/**
 * Update user role
 * Requires admin role
 */
export const updateUserRole = async (userId, role) => {
  try {
    const adminUpdateUserRole = httpsCallable(functions, 'adminUpdateUserRole');
    const result = await adminUpdateUserRole({ userId, role });
    return result.data;
  } catch (error) {
    console.error('Error updating user role:', error);
    if (error.code === 'functions/unauthenticated') {
      throw new Error('You must be logged in to update user roles');
    }
    if (error.code === 'functions/permission-denied') {
      throw new Error('Admin privileges required');
    }
    if (error.code === 'functions/invalid-argument') {
      throw new Error(error.message || 'Invalid request');
    }
    if (error.code === 'functions/not-found') {
      throw new Error('User not found');
    }
    throw error;
  }
};

/**
 * Delete a user
 * Requires admin role
 */
export const deleteUser = async (userId) => {
  try {
    const adminDeleteUser = httpsCallable(functions, 'adminDeleteUser');
    const result = await adminDeleteUser({ userId });
    return result.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    if (error.code === 'functions/unauthenticated') {
      throw new Error('You must be logged in to delete users');
    }
    if (error.code === 'functions/permission-denied') {
      throw new Error('Admin privileges required');
    }
    if (error.code === 'functions/invalid-argument') {
      throw new Error(error.message || 'Invalid request');
    }
    if (error.code === 'functions/not-found') {
      throw new Error('User not found');
    }
    throw error;
  }
};

// Export as object for compatibility with API pattern
const adminService = {
  getAll: getAllUsers,
  updateRole: updateUserRole,
  delete: deleteUser
};

export default adminService;
